import React, { useState } from 'react'
import { makeStyles, Menu, MenuItem, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import { formatPhoneNumber, getQuoteTypeOptions } from '@paintscout/util/builder'
import find from 'lodash/find'
import get from 'lodash/get'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import PhoneIcon from '@material-ui/icons/PhoneOutlined'
import MailIcon from '@material-ui/icons/MailOutline'
import { useQuote } from '../../../../context'
import { openMailto, openTel } from '../../../../util/deepLinks'

const useStyles = makeStyles<Theme>(
  (theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    return {
      root: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        marginTop: theme.spacing(3),
        [theme.breakpoints.down('xs')]: {
          flexWrap: 'wrap'
        }
      },
      company: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        minWidth: 0
      },
      companyLogo: {
        maxHeight: 24,
        maxWidth: 90,
        objectFit: 'contain',
        flexShrink: 0
      },
      companyName: {
        color: neutral.P50,
        fontSize: theme.typography.pxToRem(14),
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      },
      // No gap: the rep button's own left padding already spaces the name like a word
      // space after "Contact" (a flex gap on top of it reads as a hole).
      contact: {
        display: 'flex',
        alignItems: 'center'
      },
      contactLabel: {
        color: neutral.P50,
        fontSize: theme.typography.pxToRem(14)
      },
      // The rep chip (name + caret) — the one interactive affordance, opening a
      // phone/email menu (mirrors the contact menu in QuoteDocumentHeader).
      repButton: {
        appearance: 'none',
        border: 'none',
        background: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        padding: theme.spacing(0.5, 0.75),
        borderRadius: theme.borderRadius.md,
        cursor: 'pointer',
        color: neutral.P40,
        fontSize: theme.typography.pxToRem(14),
        fontWeight: theme.typography.fontWeightMedium,
        transition: 'background-color 150ms ease',
        '&:hover': {
          backgroundColor: neutral.P96
        },
        '&:focus-visible': {
          outline: `2px solid ${theme.paintscout.palette.brand.Blue.Blue55}`,
          outlineOffset: 2
        }
      },
      caret: {
        fontSize: 18,
        color: neutral.P60
      }
    }
  },
  { name: 'GatePreviewFooter' }
)

/**
 * Customer-facing footer for the tiered-package gate: company identity on the left, and
 * "Questions? Contact <sales rep>" on the right, whose caret opens a dropdown with the rep's
 * phone and email (tel:/mailto:). The sales rep is the quote owner, mirroring QuoteDocumentHeader.
 */
export default function GatePreviewFooter() {
  const classes = useStyles({})
  const { quote } = useQuote()
  const { options } = useClientOptions()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const co = options.options
  const quoteType = find(getQuoteTypeOptions({ options }), { key: quote?.int_ext?.value as any })
  const override = quoteType?.overrideCompanyOptions
  const companyName = String((override ? quoteType?.companyOptions?.name : co.companyName?.value) ?? '')
  const logo = override ? get(quoteType, 'companyOptions.logo.value', '') : co.logo?.value

  const owner = quote?.owner
  const estimatorName = owner ? [owner.firstName, owner.lastName].filter(Boolean).join(' ') : null
  const estimatorEmail = owner?.email ?? null
  const estimatorPhone = owner?.phoneNumber
    ? formatPhoneNumber({ options, phoneNumber: owner.phoneNumber, extension: owner.phoneExtension })
    : null

  if (!logo && !companyName && !owner) {
    return null
  }

  const hasMenu = !!estimatorPhone || !!estimatorEmail

  return (
    <div className={classes.root}>
      <div className={classes.company}>
        {logo && <img className={classes.companyLogo} src={logo} alt={companyName || 'Company logo'} />}
        {companyName && <Typography className={classes.companyName}>{companyName}</Typography>}
      </div>

      {owner && (
        <div className={classes.contact}>
          <Typography className={classes.contactLabel}>Questions? Contact</Typography>
          <button
            type="button"
            className={classes.repButton}
            onClick={hasMenu ? (e) => setAnchorEl(e.currentTarget) : undefined}
            aria-haspopup={hasMenu ? 'true' : undefined}
            disabled={!hasMenu}
          >
            {estimatorName && <span>{estimatorName}</span>}
            {hasMenu && <KeyboardArrowDownIcon className={classes.caret} />}
          </button>
          {hasMenu && (
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
              {estimatorPhone && (
                <MenuItem
                  icon={PhoneIcon}
                  onClick={() => {
                    setAnchorEl(null)
                    openTel(estimatorPhone)
                  }}
                >
                  {estimatorPhone}
                </MenuItem>
              )}
              {estimatorEmail && (
                <MenuItem
                  icon={MailIcon}
                  onClick={() => {
                    setAnchorEl(null)
                    openMailto(estimatorEmail)
                  }}
                >
                  {estimatorEmail}
                </MenuItem>
              )}
            </Menu>
          )}
        </div>
      )}
    </div>
  )
}
